<template>
    <div class="mt-4 flex flex-col items-start sm:flex-row sm:items-center sm:justify-between">
        <div
            class="inline-flex w-full lg:w-auto rounded bg-slate-200 p-1 mb-2 sm:mb-0"
        >
            <button
                v-for="option in platformContentTypes[platformId]"
                :key="option.id"
                class="flex grow rounded px-3 py-2 sm:px-4 font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                :class="{
                    'bg-white text-foreground': filters.contentType === option.id,
                }"
                @click.prevent="filters.contentType = option.id"
            >
                <span class="mx-auto text-sm capitalize">{{ option.label }}</span>
            </button>
        </div>

        <div
            v-if="fixedDayRange"
            class="inline-flex w-full lg:w-auto rounded bg-slate-200 p-1"
            :class="{
                'sm:ml-auto': platformContentTypes[platformId].length <= 1,
            }"
            role="group"
        >
            <button
                v-for="metricWindow in metricsWindows"
                :key="metricWindow.dayRange"
                class="flex grow rounded px-3 py-2 sm:px-4 font-semibold leading-tight transition duration-150 ease-in-out focus:outline-none focus:ring-0 hover:text-foreground"
                :class="{
                    'bg-white text-foreground': filters.dayRange === metricWindow.dayRange,
                }"
                @click.prevent="filters.dayRange = metricWindow.dayRange"
                @click="onGenerateReport(false)"
            >
                <span class="mx-auto text-sm capitalize">{{ getFromDictionary(`superview.metricWindow.${metricWindow.title}`) }}</span>
            </button>
        </div>
    </div>

    <div v-if="!fixedDayRange" class="mt-4 flex rounded-sm border border-slate-200 bg-white px-5 py-4">
        <div class="flex flex-col">
            <div class="flex">
                <label class="mb-auto mt-auto text-sm font-medium">Content Tags</label>

                <CodeSelect
                    style="width: 299px"
                    class="ml-14"
                    title=""
                    code-type="tag"
                    :preload="true"
                    select-mode="single"
                    :max-items="5000"
                    search-placeholder="Search for a tag"
                    :is-caret-enabled="true"
                    :platform="platformId"
                    :display-options="{
                        platform_id: platformId,
                        content_type: filters.contentType,
                        metrics_type: 'channels',
                        sort_by: `platforms.${platformId}.metrics.${filters.contentType}.channels`,
                        sort_dir: 'desc',
                    }"
                    id-field="name"
                    no-results-text="All matches selected"
                    @selection-change="(item) => onTagChange(item)"
                />
            </div>

            <div v-if="['twitch', 'youtube'].includes(platformId)" class="mt-4 flex">
                <label class="mb-auto mt-auto text-sm font-medium">Game</label>

                <CodeSelect
                    style="width: 299px"
                    class="ml-14"
                    title=""
                    code-type="game"
                    :preload="true"
                    select-mode="single"
                    :max-items="5000"
                    search-placeholder="Search for a game"
                    :is-caret-enabled="true"
                    :platform="platformId"
                    :display-options="{
                        platform_id: platformId,
                        content_type: filters.contentType,
                        metrics_type: 'channels',
                    }"
                    no-results-text="All matches selected"
                    @selection-change="(item) => onGameChange(item)"
                />
            </div>

            <div class="mt-4 flex">
                <div class="flex">
                    <label class="mb-auto mt-auto text-sm font-medium">Between</label>

                    <div class="relative ml-8 mr-4 pl-1">
                        <flat-pickr
                            v-model="filters.startAt"
                            class="form-input w-full pl-9 font-medium text-slate-500 hover:text-slate-600 focus:border-slate-300"
                            :config="pickerConfig"
                        />
                        <div class="pointer-events-none absolute inset-0 right-auto flex items-center">
                            <svg class="ml-3 h-4 w-4 fill-current text-slate-500" viewBox="0 0 16 16">
                                <path
                                    d="M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1zm-1 12H2V6h12v8z"
                                />
                            </svg>
                        </div>
                    </div>
                    <span class="mb-auto mt-auto align-middle text-sm font-medium">and</span>
                    <div class="relative ml-4">
                        <flat-pickr
                            v-model="filters.endAt"
                            class="form-input w-full pl-9 font-medium text-slate-500 hover:text-slate-600 focus:border-slate-300"
                            :config="pickerConfig"
                        />
                        <div class="pointer-events-none absolute inset-0 right-auto flex items-center">
                            <svg class="ml-3 h-4 w-4 fill-current text-slate-500" viewBox="0 0 16 16">
                                <path
                                    d="M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1zm-1 12H2V6h12v8z"
                                />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex grow items-end justify-end">
            <button
                :disabled="!canGenerateReport"
                class="btn text-white enabled:bg-indigo-500 enabled:hover:bg-indigo-600 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                @click="onGenerateReport(false)"
            >
                <span>Create Report</span>
                <Tooltip v-if="!canGenerateReport" class="my-auto mr-2 box-content">
                    <template #trigger>
                        <AppIcon
                            class="ml-2 h-4 w-4 text-slate-400 transition duration-200 hover:text-slate-500"
                            name="circle-info"
                        />
                    </template>
                    <template #default>
                        <div class="font-bold">
                            <div class="space-y-2">
                                <p>To generate a report, please select a game and a time range up to 35 days.</p>
                            </div>
                        </div>
                    </template>
                </Tooltip>
            </button>
        </div>
    </div>

    <div v-if="!fixedDayRange && !state.hasGenerated"></div>

    <div v-else class="mt-4">
        <DiscoverTable
            :paging="paging"
            :creators="creators"
            :is-loading="!state.isLoaded"
            :is-busy="!state.isLoaded"
            :columns="platformColumns"
            :enabled-columns="table.enabledColumns"
            :platform-id="platformId"
            :is-selectable="true"
            id-prop="id"
            superview-path="partialview"
            :list-id="list?.id"
            :is-editable="isEditable"
            @enabled-column-change="onEnabledColumnChange"
            @filter-change="onFilterChange"
            @paging-change="onPagingChange"
            @export-creators="onExportCreators"
            @send-email="$emit('send-email', $event)"
            @remove-from-list="
                (creatorsToRemove) => {
                    $emit('remove-from-list', creatorsToRemove)
                }
            "
            @selected-creators-change="onSelectedCreatorsChange"
        >
            <template #header-toolbar>
                <ColumnDisplayManager
                    :available-columns="platformColumns"
                    :groups="availableColumnGroups"
                    :model-value="table.enabledColumns"
                    @update:model-value="(newEnabledColumns) => onEnabledColumnChange(newEnabledColumns)"
                />
            </template>

            <template #creator="{ item }">
                <DiscoveryProfileUnlock
                    :platform-id="platformId"
                    :platform-user-id="item.id"
                    :modal="'creator'"
                    :logo="item.partialview.normalized.avatar_url"
                    :name="item.partialview.normalized.name"
                    :display-name="item.partialview.normalized.display_name"
                />
            </template>

            <template #affinity="{ item, field, column }">
                <AffinityPlate
                    :affinity-percent="field"
                    :class="{
                        'opacity-25': column.is_disabled,
                    }"
                    :metadata="metadata"
                    :affinity="item.affinity"
                    :discover-entry="item"
                />
            </template>

            <template #email="{ item }">
                <EmailUnlock :platform-id="item.platform_id" :platform-user-id="item.id" />
            </template>

            <template #platforms="{ field }">
                <div class="relative flex w-full gap-2">
                    <router-link
v-for="social in field.platformSocials" :key="social.platformId"
                        :to="{
                            path: $route.path,
                            query: {
                                ...$route.query,
                                modal: 'creator',
                                platform_id: social.platform_id,
                                platform_user_id: social.platform_user_id,
                            },
                        }"
                        class="align-center flex z-0"
                    >
                        <NamePlate
                            class="my-auto"
                            :logo="social.avatar_url"
                            :name="social.platform_user_name"
                            :display-name="social.display_name"
                            :platform-id="social.platform_id"
                            :platform-user-id="social.platform_user_id"
                            :is-drawer-enabled="false"
                            :is-interactive="true"
                            :hideName="true"
                        />
                    </router-link>
                </div>
            </template>
        </DiscoverTable>
    </div>
</template>

<script>
import { MEDIA_CDN_BASE } from '@/utils/media'
import _ from 'lodash'
import numeral from 'numeral'
import { mapGetters } from 'vuex'
import { nextTick } from 'vue'
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'
import CodeSelect from '@/components/CodeSelect.vue'
import reportsAPI from '@/utils/api/api.reports'
import Tooltip from '@/components/Tooltip.vue'

import DiscoverTable from '@/partials/tables/DiscoverTable.vue'
import AffinityPlate from '@/components/plates/AffinityPlate.vue'
import EmailUnlock from '@/components/unlocks/EmailUnlock.vue'
import ColumnDisplayManager from '@/partials/options/ColumnDisplayManager.vue'
import DiscoveryProfileUnlock from '@/components/unlocks/DiscoveryProfileUnlock.vue'
import NamePlate from '@/components/plates/NamePlate.vue'

const platforms = ['twitch', 'youtube', 'tiktok', 'twitter', 'instagram']
export default {
    name: 'ListReport',

    components: {
        CodeSelect,
        flatPickr,
        Tooltip,
        DiscoverTable,
        AffinityPlate,
        EmailUnlock,
        ColumnDisplayManager,
        DiscoveryProfileUnlock,
        NamePlate,
    },

    props: {
        list: {
            type: Object,
        },
        platformId: {
            type: String,
        },
        fixedDayRange: {
            type: Boolean,
        },
        searchText: {
            type: String,
        },
        isEditable: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['remove-from-list', 'send-email'],

    data() {
        return {
            creators: [],
            selectedCreators: [],
            state: {
                hasGenerated: false,
                isLoaded: true,
                table: {
                    columns: [],
                    enabledColumns: [],
                },
            },
            paging: {
                pages: 1,
                perPage: 25,
                currentPage: 1,
                totalPages: 1,
                sort: [
                    {
                        field: 'partialview.normalized.name',
                        direction: 'asc',
                        source: 'default',
                    },
                ],
            },
            filters: {
                game: null,
                startAt: moment().add(-8, 'days').add(-moment().minute(), 'minutes').toDate(),
                endAt: moment().add(-moment().minute(), 'minutes').toDate(),
                contentType: '',
                dayRange: 30,
            },
            pickerConfig: {
                enableTime: true,
                dateFormat: 'Z',
                altFormat: 'F j, Y h:i K',
                altInput: true,
                maxDate: moment().add(-moment().minute(), 'minutes').toDate(),
            },
            table: {
                enabledColumns: [],
                defaultEnabledColumns: {
                    twitch: [
                        'partialview.normalized.name',
                        'partialview.normalized.followers',
                        'summary.avg_viewers',
                        'summary.sum_duration_seconds',
                        'summary.content_count',
                        'language',
                        'country',
                    ],
                    youtube: [
                        'partialview.normalized.name',
                        'partialview.normalized.followers',
                        'summary.avg_views',
                        'summary.sum_views',
                        'summary.sum_comments',
                        'language',
                        'country',
                    ],
                    tiktok: [
                        'partialview.normalized.name',
                        'partialview.normalized.followers',
                        'summary.content_count',
                        'summary.avg_views',
                        'summary.sum_views',
                        'summary.peak_views',
                        'summary.sum_likes',
                        'summary.sum_comments',
                        'summary.sum_shares',
                        'summary.avg_engagement_rate',
                        'language',
                        'country',
                    ],
                    twitter: [
                        'partialview.normalized.name',
                        'partialview.normalized.followers',
                        'summary.avg_views',
                        'summary.sum_views',
                        'summary.peak_views',
                        'summary.content_count',
                        'summary.sum_likes',
                        'summary.sum_shares',
                        'summary.sum_comments',
                        'summary.sum_quotes',
                        'summary.avg_engagement_rate',
                        'language',
                        'country',
                    ],
                    instagram: [
                        'partialview.normalized.name',
                        'partialview.normalized.followers',
                        'summary.avg_views',
                        'summary.content_count',
                        'summary.sum_comments',
                        'language',
                        'country',
                    ],
                },
                columns: [
                    {
                        id: 'partialview.normalized.name',
                        key: 'partialview',
                        header: {
                            label: 'Creator',
                            is_sortable: true,
                            is_hideable: false,
                            class: '',
                        },
                        row: {
                            class: '',
                            slot: 'creator',
                            slotTransformer: (item) => ({
                                id: _.get(item, 'partialview._id'),
                                name: _.get(item, 'partialview.normalized.name'),
                                display_name: _.get(item, 'partialview.normalized.display_name'),
                                logo: `${MEDIA_CDN_BASE}/${
                                    this.platformId
                                }/avatars/small/${_.get(item, 'partialview._id')}`,
                            }),
                        },
                        is_sticky: true,
                        is_exportable: true,
                        exportTransformer: (item) => ({
                            Creator:
                                _.get(item, 'partialview.normalized.display_name') ||
                                _.get(item, 'partialview.normalized.name'),
                        }),
                        enabled: () => {
                            return platforms.includes(this.platformId)
                        },
                        group: 'basic',
                    },
                    {
                        id: 'partialview.normalized.display_name',
                        key: 'partialview.normalized.display_name',
                        header: {
                            label: 'Display Name',
                            is_sortable: true,
                            is_hideable: false,
                            class: '',
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => field || '-',
                        },
                        is_exportable: true,
                        exportTransformer: (item) => ({
                            'Display Name': _.get(item, 'partialview.normalized.display_name'),
                        }),
                        enabled: () => true,
                        group : 'basic',
                    },
                    {
                        id: 'summary.content_count',
                        key: 'summary.content_count',
                        header: {
                            label: 'Content Count',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Content count',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of pieces of content made during the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field !== null && field !== undefined) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} ${this.contentTypeLabel}`]: this.$filters.formatWholeNumberWithUnit(
                                this.formatExportNumber(_.get(item, 'summary.content_count', null), 0),
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.avg_viewers',
                        key: 'summary.avg_viewers',
                        header: {
                            label: 'Avg Viewers',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Average Concurrent Viewers',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The average number of viewers you would expect to see in this creator\'s stream at any point within the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field, item }) => this.formatTwitchStreamMetric(field, item),
                        },
                        is_exportable: true,
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Avg Viewers`]: this.formatTwitchStreamMetricForExport(
                                _.get(item, 'summary.avg_viewers', null),
                                item,
                            ),
                        }),
                        enabled: () => {
                            return ['twitch'].includes(this.platformId)
                        },
                        group: 'platform',
                    },
                    {
                        id: 'summary.peak_viewers',
                        key: 'summary.peak_viewers',
                        header: {
                            label: 'Peak Viewers',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Peak Concurrent Viewers',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The maximum number of viewers that were in this creator\'s stream at any point within the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field, item }) => this.formatTwitchStreamMetric(field, item),
                        },
                        is_exportable: true,
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Peak Viewers`]: this.formatTwitchStreamMetricForExport(
                                _.get(item, 'summary.peak_viewers', null),
                                item,
                            ),
                        }),
                        enabled: () => {
                            return ['twitch'].includes(this.platformId)
                        },
                        group: 'platform',
                    },
                    {
                        id: 'summary.avg_chatters',
                        key: 'summary.avg_chatters',
                        header: {
                            label: 'Avg Chatters',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Average Concurrent Chatters',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The average number of chatters you would expect to see in this creator\'s stream at any point within the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field, item }) => this.formatTwitchStreamMetric(field, item),
                        },
                        is_exportable: true,
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Avg Chatters`]: this.formatTwitchStreamMetricForExport(
                                _.get(item, 'summary.avg_chatters', null),
                                item,
                            ),
                        }),
                        enabled: () => {
                            return ['twitch'].includes(this.platformId)
                        },
                        group: 'platform',
                    },
                    {
                        id: 'summary.sum_duration_seconds',
                        key: 'summary.sum_duration_seconds',
                        header: {
                            label: 'Hours streamed',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Hours streamed',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of hours streamed during the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field) {
                                    return this.$filters.formatWholeNumberWithUnit(field / 60 / 60)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return ['twitch'].includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Hours Streamed`]: this.formatExportNumber(
                                _.get(item, 'summary.sum_duration_seconds', 0) / 60 / 60,
                                1,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.avg_views',
                        key: 'summary.avg_views',
                        header: {
                            label: 'AVG Views',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Average views',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The average number of views per video during the period.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return ['youtube', 'tiktok', 'twitter', 'instagram'].includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Avg Views`]: this.formatExportNumber(
                                _.get(item, 'summary.avg_views', null),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.sum_views',
                        key: 'summary.sum_views',
                        header: {
                            label: 'Total Views',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Total views',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of views for videos during the period.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return ['youtube', 'tiktok', 'twitter', 'instagram'].includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Total Views`]: this.formatExportNumber(
                                _.get(item, 'summary.sum_views', null),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.peak_views',
                        key: 'summary.peak_views',
                        header: {
                            label: 'Peak Views',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Peak views',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The peak number of views for videos during the period.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return ['youtube', 'tiktok', 'twitter', 'instagram'].includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Peak Views`]: this.formatExportNumber(
                                _.get(item, 'summary.peak_views', null),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.sum_likes',
                        key: 'summary.sum_likes',
                        header: {
                            label: 'Likes',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Likes',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of likes during the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field !== null && field !== undefined) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return this.platformId !== 'twitch'
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Likes`]: this.formatExportNumber(
                                _.get(item, 'summary.sum_likes', null),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.sum_shares',
                        key: 'summary.sum_shares',
                        header: {
                            label: 'Shares',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Shares',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of shares during the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field !== null && field !== undefined) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return !['twitch', 'youtube', 'instagram'].includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} ${this.platformId === 'twitter' ? 'Reposts' : 'Shares'}`]: this.formatExportNumber(
                                _.get(item, 'summary.sum_shares', null),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.sum_quotes',
                        key: 'summary.sum_quotes',
                        header: {
                            label: 'Quotes',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Quotes',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of quotes during the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field !== null && field !== undefined) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return ''
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return this.platformId === 'twitter'
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Quotes`]: this.formatExportNumber(
                                _.get(item, 'summary.sum_quotes'),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.sum_comments',
                        key: 'summary.sum_comments',
                        header: {
                            label: 'Comments',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Comments',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The total number of comments during the aggregation window.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field !== null && field !== undefined) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return ''
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return ['youtube', 'tiktok', 'twitter', 'instagram'].includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Comments`]: this.formatExportNumber(
                                _.get(item, 'summary.sum_comments'),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'summary.avg_engagement_rate',
                        key: 'summary.avg_engagement_rate',
                        header: {
                            label: 'Engagement Rate',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Average Engagement Rate',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The average percentage of viewers that decide to interact with the creator\'s content.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => this.formatEngagementRate(field),
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Engagement Rate`]: this.formatEngagementRate(
                                _.get(item, 'summary.avg_engagement_rate'),
                                null,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'partialview.normalized.followers',
                        key: 'partialview.normalized.followers',
                        header: {
                            label: 'Followers',
                            sub_label: '',
                            is_sortable: true,
                            tooltip: {
                                title: 'Followers',
                                sub_title: '',
                                content: [
                                    {
                                        text: 'The channel\'s current number of followers.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field) {
                                    return this.$filters.formatWholeNumberWithUnit(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} ${this.platformId === 'youtube' ? 'Subscribers' : 'Followers'}`]: this.formatExportNumber(
                                _.get(item, 'partialview.normalized.followers', null),
                                0,
                            ),
                        }),
                        group: 'platform',
                    },
                    {
                        id: 'language',
                        key: 'partialview.normalized.language',
                        header: {
                            label: 'Language',
                            is_sortable: false,
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => field && this.getLanguageFromCode(field) !== 'Unknown' ? this.getLanguageFromCode(field) : '',
                        },
                        is_exportable: true,
                        enabled: () => {
                            return platforms.includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            Language: _.get(item, 'partialview.normalized.language', '') && this.getLanguageFromCode(_.get(item, 'partialview.normalized.language', '')) !== 'Unknown' ? this.getLanguageFromCode(_.get(item, 'partialview.normalized.language', '')) : '',
                        }),
                        group: 'basic',
                    },
                    {
                        id: 'country',
                        key: 'partialview.normalized.country',
                        header: {
                            label: 'Country',
                            is_sortable: false,
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => field ? this.getCountryFromCode(field) : '',
                        },
                        is_exportable: true,
                        enabled: () => {
                            return platforms.includes(this.platformId)
                        },
                        exportTransformer: (item) => ({
                            Country: _.get(item, 'partialview.normalized.country', '') ? this.getCountryFromCode(_.get(item, 'partialview.normalized.country', '')) : '',
                        }),
                        group: 'basic',
                    },
                    {
                        id: 'email',
                        key: 'partialview.normalized.email',
                        header: {
                            label: 'Email',
                            is_sortable: false,
                        },
                        row: {
                            class: '',
                            slot: 'email',
                        },
                        is_exportable: true,
                        enabled: () => true,
                        exportTransformer: (item) => ({
                            Email: _.get(item, 'partialview.normalized.email', ''),
                        }),
                        group: 'basic',
                    },
                    {
                        id: 'lastmessage',
                        key: 'messages.last_messaged_at',
                        header: {
                            label: 'Last message',
                            is_sortable: false,
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field) {
                                    return this.$filters.formatDateAndTime(field)
                                }
                                return '-'
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            LastMessage: _.get(item, 'messages.last_messaged_at', ''),
                        }),
                        group: 'basic',
                    },
                    {
                        id: 'gender',
                        key: 'partialview.normalized.gender',
                        header: {
                            label: 'Gender',
                            is_sortable: true,
                            tooltip: {
                                title: 'Gender',
                                content: [
                                    {
                                        text: 'This is an estimate made by AI to determine the gender of the creator.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => {
                                if (field && field.length > 0) {
                                    return field.charAt(0).toUpperCase() + field.slice(1)
                                }
                                return field
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            Gender: _.startCase(_.get(item, 'partialview.normalized.gender', '')),
                        }),
                        group: 'basic',
                    },
                    {
                        id: 'age_range',
                        key: 'partialview.normalized.age_range',
                        header: {
                            label: 'Age Range',
                            is_sortable: true,
                            tooltip: {
                                title: 'Age Range',
                                content: [
                                    {
                                        text: 'This is an estimate made by AI to determine the age range of the creator.',
                                    },
                                ],
                            },
                        },
                        row: {
                            class: '',
                            formatter: ({ field }) => field,
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            'Age Range': _.get(item, 'partialview.normalized.age_range', ''),
                        }),
                        group: 'basic',
                    },
                    {
                        id: 'platforms',
                        key: 'partialview.normalized.socials',
                        header: {
                            label: 'Profile Links',
                            is_sortable: false,
                        },
                        row: {
                            class: '',
                            slot: 'platforms',
                            slotTransformer: (item) => {
                                const socials = item?.partialview?.normalized?.socials ?? {}

                                const platformLinksArray = Object.entries(socials)
                                    .filter(([key]) => platforms.includes(key))
                                    .map(([platform, social]) => ({
                                        platform_id : social.platform_id,
                                        platform_user_id : social.platform_user_id,
                                        platform_user_name : social.platform_user_name,
                                        display_name : social.display_name,
                                        link: social.link,
                                        avatar_url: social.avatar_url
                                    }))
                                // Sort the array to prioritize the one matching this.platformId first
                                // Then follow the order from `platforms`
                                const platformSocials = platformLinksArray.sort((a, b) => {
                                    if (a.platform_id === this.platform_id) return -1
                                    if (b.platform_id === this.platform_id) return 1
                                    return platforms.indexOf(a.platform_id) - platforms.indexOf(b.platform_id)
                                })
                                return { platformSocials }
                            },
                        },
                        is_exportable: true,
                        enabled: () => {
                            return true
                        },
                        exportTransformer: (item) => ({
                            [`${this.platformLabel} Link`]:
                                _.get(item, `partialview.normalized.socials.${this.platformId}.link`) ||
                                _.get(item, 'partialview.normalized.link', ''),
                        }),
                        group: 'basic',
                    }
                ],
                columnGroups: [
                    { id: 'basic', label: 'Basic' },
                    { id: 'metrics', label: 'Metrics' },
                    { id: 'platforms', label: 'Socials' },
                ],
            },

            columnGroups: [
                { id: 'basic', label: 'Basic', platforms : ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
                { id: 'metrics', label: 'Metrics', platforms : ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
                // { id: 'audience', label: 'Audience', platforms : ['youtube', 'twitch', 'tiktok', 'twitter', 'instagram'] },
                // { id: 'games', label: 'Games', platforms : ['youtube', 'twitch'] },
            ],
            platformContentTypes: {
                twitch: [{ id: 'stream', label: 'Stream', isDefault: true }],
                youtube: [
                    { id: 'video', label: 'Video', isDefault: true },
                    { id: 'short', label: 'Short' },
                    { id: 'stream', label: 'Stream' },
                ],
                tiktok: [{ id: 'video', label: 'Short', isDefault: true }],
                twitter: [{ id: 'post', label: 'Post', isDefault: true }],
                instagram: [{ id: 'post', label: 'Post', isDefault: true }],
            },
            metricsWindows: [
                { dayRange: 30, title: 'day_30' },
                { dayRange: 90, title: 'day_90' },
                { dayRange: 365, title: 'day_365' },
            ],
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary', 'isSfStaff', 'getLanguageFromCode', 'getCountryFromCode', 'preferences']),
        canGenerateReport() {
            return (
                (this.filters.game || this.filters.tag) &&
                this.filters.startAt &&
                this.filters.endAt &&
                Math.abs(moment(this.filters.startAt).diff(moment(this.filters.endAt), 'days')) <= 36
            )
        },

        platformLabel() {
            return this.getFromDictionary(`platforms.toProper.${this.platformId}`)
        },

        contentTypeLabel() {
            return this.getFromDictionary(`superview.contentType.switcher.${this.platformId}.${this.filters.contentType}`)
        },

        metricWindowName() {
            if (!this.fixedDayRange && this.filters.startAt && this.filters.endAt) {
                return `${moment(this.filters.startAt).format('MMM D, YYYY')} - ${moment(this.filters.endAt).format('MMM D, YYYY')}`
            }

            const currentMetricWindow = _.find(this.metricsWindows, window => {
                return window.dayRange === this.filters.dayRange
            })

            if (currentMetricWindow) {
                return this.getFromDictionary(`superview.metricWindow.${currentMetricWindow.title}`)
            }

            return `${this.filters.dayRange} day`
        },

        platformMetricSubLabel() {
            return `${this.platformLabel} - ${this.metricWindowName}`
        },

        platformMetricColumnLabels() {
            return {
                'summary.content_count': this.contentTypeLabel,
                'summary.avg_viewers': 'Avg Viewers',
                'summary.peak_viewers': 'Peak Viewers',
                'summary.avg_chatters': 'Avg Chatters',
                'summary.sum_duration_seconds': 'Hours Streamed',
                'summary.avg_views': 'Avg Views',
                'summary.sum_views': 'Total Views',
                'summary.peak_views': 'Peak Views',
                'summary.sum_likes': 'Likes',
                'summary.sum_shares': this.platformId === 'twitter' ? 'Reposts' : 'Shares',
                'summary.sum_quotes': 'Quotes',
                'summary.sum_comments': 'Comments',
                'summary.avg_engagement_rate': 'Engagement Rate',
                'partialview.normalized.followers': this.platformId === 'youtube' ? 'Subscribers' : 'Followers',
            }
        },

        platformColumns() {
            return _.map(_.filter(this.table.columns, (column) => column.enabled() === true), (column) => {
                if (column.group !== 'platform') {
                    return column
                }

                const header = {
                    ...column.header,
                    sub_group_label: this.platformLabel,
                }
                const metricLabel = this.platformMetricColumnLabels[column.id]

                if (metricLabel) {
                    header.label = metricLabel
                    header.sub_label = this.platformMetricSubLabel

                    if (header.tooltip) {
                        header.tooltip = {
                            ...header.tooltip,
                            sub_title: this.platformMetricSubLabel,
                        }
                    }
                }

                return {
                    ...column,
                    header,
                }
            })
        },

        availableColumnGroups() {
            return [
                { id: 'basic', label: 'Basic' },
                { id: 'platform', label: this.platformLabel },
            ]
        },

        platformExportColumns() {
            const baseColumnIds = [
                'partialview.normalized.name',
                'partialview.normalized.display_name',
                'language',
                'country',
                'email',
                'gender',
                'age_range',
                'lastmessage',
            ]
            const columnsById = _.keyBy(this.platformColumns, 'id')
            const baseColumns = _.compact(baseColumnIds.map((columnId) => columnsById[columnId]))
            const metricColumns = _.compact(
                _.keys(this.platformMetricColumnLabels).map((columnId) => columnsById[columnId]),
            )
            const profileLinksColumn = columnsById.platforms

            return _.compact([
                ...baseColumns,
                ...metricColumns,
                profileLinksColumn,
            ])
        },
    },

    watch: {
        list() {
            this.reset()
        },
        platformId() {
            this.reset()
        },
        fixedDayRange() {
            this.reset()
        },
        searchText() {
            this.paging.currentPage = 1
            this.debounceGenerate()
        },
        'filters.contentType': function () {
            this.paging.currentPage = 1
            this.debounceGenerate()
        },
    },

    created() {
        this.setEnabledColumnDefaults()
        this.reset()
    },

    methods: {
        async copyToClipboard(event, textToCopy) {
            try {
                await navigator.clipboard.writeText(textToCopy)

                const overlayMessage = event.target

                if (overlayMessage) {
                    overlayMessage.classList.add('opacity-100')

                    setTimeout(() => {
                        overlayMessage.classList.remove('opacity-100')
                    }, 2500)
                } else {
                    console.error('Overlay message element not found.')
                }
            } catch (err) {
                console.error('Failed to copy text: ', err)
            }
        },

        getDefaultExportColumns() {
            const selectedColumnIds = new Set(this.table.enabledColumns)

            return _.filter(this.platformExportColumns, (column) => selectedColumnIds.has(column.id))
        },

        reset() {
            this.paging.currentPage = 1
            this.creators = []
            this.state.hasGenerated = false
            this.state.isLoaded = false
            this.filters.contentType = _.find(this.platformContentTypes[this.platformId], (contentType) => {
                return contentType.isDefault
            })?.id
            this.setEnabledColumnDefaults()
            this.debounceGenerate()
        },

        debounceGenerate: _.debounce(function () {
            nextTick(() => {
                if (this.fixedDayRange) {
                    this.onGenerateReport(false, false)
                }
            })
        }, 250),

        onGameChange(games) {
            this.filters.game = _.first(games)
        },

        onTagChange(tags) {
            this.filters.tag = _.first(tags)
        },

        onChangeSort(sortCol) {
            if (sortCol === this.paging.sort.sortBy) {
                if (this.paging.sort.sortDir === 'asc') {
                    this.paging.sort.sortDir = ''
                    this.paging.sort.sortBy = ''
                } else {
                    this.paging.sort.sortDir = 'asc'
                }
            } else {
                this.paging.sort.sortBy = sortCol
                this.paging.sort.sortDir = 'desc'
            }
        },

        async onGenerateReport(isExport, isPreExport) {
            if (!this.list || !this.list.id) {
                return null
            }

            if (!isExport) {
                this.state.hasGenerated = true
                this.state.isLoaded = false
            }

            const options = {}
            const filters = []

            if (isExport && this.selectedCreators?.length > 0) {
                options.selectedItems = _.map(this.selectedCreators, creator => {
                    return [
                        { field : 'platform_id', value : creator.platform_id },
                        { field : 'id', value : creator.platform_user_id },
                    ]
                })
            }

            options.contentType = this.filters.contentType

            if (this.fixedDayRange) {
                options.dayRange = this.filters.dayRange
            } else {
                options.tags = this.filters?.tag?.id ? [this.filters?.tag?.id] : []
                options.gameId = this.filters?.game?.id
                options.gameName = this.filters?.game?.name
                options.startAt = this.filters.startAt
                options.endAt = this.filters.endAt
            }

            if (this.searchText) {
                filters.push({ id: 'partialview.normalized.display_name', value: this.searchText })
            }

            this.$store.dispatch('CANCEL_PENDING_REQUESTS_BY_REGEX', '^/lists/[^/]+/report$')

            return reportsAPI
                .getListReport(this.$axios, this.list.id, this.platformId, this.paging, options, filters, isExport, isPreExport)
                .then((response) => {
                    if (response.isQuotaError) {
                        return { isQuotaError: response.isQuotaError, error : response?.error }
                    }

                    if (isExport) {
                        return response.value
                    }

                    if (!response.success) return false

                    const report = response.value

                    if (this.list.id !== report?.listId) {
                        return false
                    }

                    this.creators = []

                    if (report.items) {
                        this.creators = report.items
                    }

                    this.paging.totalRecords = report.paging.totalRecords
                    this.paging.totalPages = report.paging.totalPages
                    this.paging.currentPage = report.paging.currentPage

                    this.state.isLoaded = true

                    return true
                })
        },

        async onFilterChange(updatedSort) {
            _.assign(this.paging.sort, updatedSort)
            await this.onGenerateReport(false, false)
        },

        async onPagingChange(updatedPaging) {
            _.assign(this.paging, updatedPaging)
            await this.onGenerateReport(false, false)
        },

        onEnabledColumnChange(newEnabledColumns) {
            this.table.enabledColumns = newEnabledColumns
            this.$store.commit('preferences:set', {
                key: `tables.listreport.${this.platformId}.columns`,
                value: this.table.enabledColumns,
            })
        },

        setEnabledColumnDefaults() {
            if (
                this.preferences?.tables?.listreport &&
                this.preferences?.tables?.listreport[this.platformId]?.columns
            ) {
                this.table.enabledColumns = this.preferences.tables.listreport[this.platformId].columns
            } else {
                this.table.enabledColumns = this.table.defaultEnabledColumns[this.platformId]
            }
        },

        onSelectedCreatorsChange(updatedSelectedCreators) {
            this.selectedCreators = _.map(updatedSelectedCreators, (selectedCreator) => {
                return {
                    platform_id: selectedCreator.platform_id,
                    platform_user_id: selectedCreator.id,
                }
            })
        },

        formatExportNumber(value, decimals) {

            if (value === null || value === undefined || value === '') {
                return null
            }

            if (decimals && decimals !== 0) {
                return value.toFixed(decimals)
            }

            return Math.round(value)
        },

        hasTwitchStreamContent(item) {
            return _.get(item, 'summary.content_count', 0) > 0
        },

        formatTwitchStreamMetric(value, item) {
            if (value === null || value === undefined || !this.hasTwitchStreamContent(item)) {
                return '-'
            }

            return this.$filters.formatWholeNumberWithUnit(value)
        },

        formatTwitchStreamMetricForExport(value, item) {
            if (!this.hasTwitchStreamContent(item)) {
                return null
            }

            return this.formatExportNumber(value, 0)
        },

        formatEngagementRate(value, emptyValue = '-') {
            if (value === null || value === undefined || value === '') {
                return emptyValue
            }

            return numeral(value).format('0.0%')
        },

        onExportCreators() {
            this.$store.commit('modal:setData', {
                modal_id: 'export_table',
                data: {
                    is_open: true,
                    file_name: `${this.list?.name || 'creators'} - ${this.platformId} creators - ${
                        this.filters.contentType
                    } content - ${this.filters.dayRange} day`,
                    columns: {
                        all: this.platformExportColumns,
                        enabled: this.getDefaultExportColumns(),
                    },
                    checkerFn: async () => {
                        const reportResponse = await this.onGenerateReport(true, true)

                        if (reportResponse?.isQuotaError) {
                            return -1
                        }

                        const { items } = reportResponse

                        const creators = _.map(items, (creator) => {
                            return ['superview', creator.platform_id, creator.id]
                        })

                        const statusChecks = await this.$store.dispatch('bulkFetchRevealStatus', {
                            quotaType : 'profile-unlock',
                            items : creators,
                        })

                        const revealable = _.filter(statusChecks, (item) => {
                            return (
                                item?.payload?.is_revealable === true && item?.payload?.is_previously_unlocked === false
                            )
                        })

                        return revealable
                    },
                    rowCount: this.paging.totalRecords,
                    providerFn: async () => {

                        const reportResponse = await this.onGenerateReport(true)
                        if (reportResponse?.isQuotaError) {
                            this.$errorHandler(reportResponse.error)
                            return -1
                        }
                        return reportResponse.items
                    },
                },
            })
        },
    },
}
</script>
